import {useEffect, useState, useCallback, useMemo} from 'react';
import {api} from '../lib/auth.js';
import {kr} from '../lib/format.js';

/**
 * Jabr's own invoices: create, post, and see the resulting voucher.
 *
 * NOTHING HERE REACHES FORTNOX. These are our books, kept so the two can be compared —
 * the boundary that vouchers and invoices are never created in the ERP from Jabr is
 * untouched.
 *
 * The account and the VAT rate are RESOLVED AS YOU TYPE and shown on the line, before
 * anything is saved. That is deliberate: 1 001 historic invoice rows say account 3001
 * while the ledger says otherwise, precisely because the posting was decided somewhere
 * the person writing the invoice could not see it.
 *
 * Money is entered in kronor and held in öre. The conversion happens at the input edge
 * and nowhere else; the ledger never sees a float.
 */

const ore = kronor => Math.round(Number(String(kronor).replace(',', '.') || 0) * 100);
const krFromOre = o => (o == null ? '' : (o / 100).toFixed(2).replace('.', ','));
const money = o => `${kr(o / 100)} kr`;

const emptyRow = () => ({article: '', description: '', quantity: 1, price: ''});

function Rows({rows, articles, onChange, onRemove, resolved}) {
	return (
		<div className="table-wrap">
			<table style={{minWidth: 860}}>
				<thead><tr>
					<th>Artikel</th><th>Beskrivning</th><th>Antal</th><th>À-pris</th>
					<th>Konto</th><th>Moms</th><th>Belopp</th><th />
				</tr></thead>
				<tbody>
					{rows.map((r, i) => {
						const res = resolved?.[i];
						return (
							<tr key={i} className={res?.error ? 'g__row--dirty' : undefined}>
								<td>
									<select className="search select--sm" value={r.article}
										onChange={e => onChange(i, {article: e.target.value})}>
										<option value="">— välj —</option>
										{articles.map(a => (
											<option key={a._id} value={a._id}>{a._id} {a.name}</option>
										))}
									</select>
								</td>
								<td>
									<input className="search" style={{width: 180}} value={r.description}
										placeholder={res?.article ?? ''}
										onChange={e => onChange(i, {description: e.target.value})} />
								</td>
								<td>
									<input className="search" style={{width: 60}} inputMode="decimal" value={r.quantity}
										onChange={e => onChange(i, {quantity: e.target.value})} />
								</td>
								<td>
									<input className="search" style={{width: 100}} inputMode="decimal" value={r.price}
										onChange={e => onChange(i, {price: e.target.value})} />
								</td>
								<td>{res?.error ? <span className="pill">fel</span> : (res?.account ?? '—')}</td>
								<td>{res?.vat_rate == null ? '—' : `${res.vat_rate} %`}</td>
								<td className="num--strong">{res?.net == null ? '—' : money(res.net)}</td>
								<td>
									<button className="btn btn--sm" onClick={() => onRemove(i)}
										disabled={rows.length === 1}>Ta bort</button>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export default function Invoices() {
	const [orgs, setOrgs] = useState([]);
	const [articles, setArticles] = useState([]);
	const [list, setList] = useState([]);
	const [org, setOrg] = useState('');
	const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
	const [rows, setRows] = useState([emptyRow()]);
	const [priced, setPriced] = useState(null);
	const [error, setError] = useState(null);
	const [busy, setBusy] = useState(false);
	const [opened, setOpened] = useState(null);

	const reload = useCallback(async () => {
		const r = await api('/api/books/invoices');
		if (r.ok) setList(await r.json());
	}, []);

	useEffect(() => {
		api('/api/organizations?role=customer').then(r => r.json()).then(setOrgs).catch(() => {});
		api('/api/articles').then(r => r.json()).then(setArticles).catch(() => {});
		reload();
	}, [reload]);

	// Priced on the server as the form changes, so the account and rate shown are the ones
	// that will actually be posted rather than a client-side guess at them.
	const payload = useMemo(() => rows
		.filter(r => r.article || r.price)
		.map(r => ({article: r.article || undefined, description: r.description || undefined,
			quantity: Number(String(r.quantity).replace(',', '.')) || 0, unit_price: ore(r.price)})),
	[rows]);

	useEffect(() => {
		if (!payload.length || !payload.some(r => r.unit_price)) { setPriced(null); setError(null); return; }
		let live = true;
		const t = setTimeout(async () => {
			const res = await api('/api/books/price', {method: 'POST',
				body: JSON.stringify({organization: org || undefined, rows: payload})});
			const j = await res.json().catch(() => ({}));
			if (!live) return;
			if (!res.ok) { setPriced({rows: j.rows ?? []}); setError(j.error); }
			else { setPriced(j); setError(null); }
		}, 200);
		return () => { live = false; clearTimeout(t); };
	}, [payload, org]);

	const setRow = (i, patch) => setRows(rs => rs.map((r, n) => (n === i ? {...r, ...patch} : r)));

	const create = async (andPost) => {
		setBusy(true); setError(null);
		try {
			const res = await api('/api/books/invoices', {method: 'POST', body: JSON.stringify({
				organization: org || undefined,
				customer_name: orgs.find(o => o._id === org)?.name,
				invoice_date: date, rows: payload,
				// A key derived from the invoice's own content: the same form submitted twice
				// is the same invoice, and the unique index refuses the second.
				idempotency_key: `${org}|${date}|${payload.map(r => `${r.article}:${r.quantity}:${r.unit_price}`).join(',')}`,
			})});
			const j = await res.json();
			if (!res.ok) { setError(j.error); return; }
			if (andPost) {
				const p = await api(`/api/books/invoices/${j._id}/post`, {method: 'POST'});
				const pj = await p.json();
				if (!p.ok) { setError(pj.error); await reload(); return; }
			}
			setRows([emptyRow()]);
			setPriced(null);
			await reload();
		} catch (e) { setError(e.message); } finally { setBusy(false); }
	};

	const post = async id => {
		const r = await api(`/api/books/invoices/${id}/post`, {method: 'POST'});
		const j = await r.json();
		if (!r.ok) setError(j.error);
		await reload();
	};

	const canSave = priced && !error && payload.length && payload.every(r => r.unit_price);

	return (
		<>
			<p className="sub">
				Fakturor i Jabrs egna böcker. Ingenting skickas till Fortnox — de här verifikaten
				är våra, och finns för att kunna jämföras. Konto och momssats härleds ur
				artikelregistret och kundens land och visas på raden innan något sparas.
			</p>

			<div className="panel">
				<div className="panel__title">Ny faktura</div>
				<div className="editgrid">
					<label className="editfield"><span>Kund</span>
						<select className="search" value={org} onChange={e => setOrg(e.target.value)}>
							<option value="">— välj kund —</option>
							{orgs.map(o => <option key={o._id} value={o._id}>{o.name}{o.country && o.country !== 'SE' ? ` (${o.country})` : ''}</option>)}
						</select>
					</label>
					<label className="editfield"><span>Fakturadatum</span>
						<input className="search" type="date" value={date} onChange={e => setDate(e.target.value)} />
					</label>
				</div>

				<Rows rows={rows} articles={articles} resolved={priced?.rows}
					onChange={setRow} onRemove={i => setRows(rs => rs.filter((_, n) => n !== i))} />

				<div className="editbar">
					<button className="btn" onClick={() => setRows(rs => [...rs, emptyRow()])}>Lägg till rad</button>
					<span className="spacer" />
					{priced && !error && (
						<span className="stamp">
							Netto {money(priced.net)} · Moms {money(priced.vat)}
							{priced.rounding ? ` · Öresavrundning ${money(priced.rounding)}` : ''}
							{' · '}<b>Att betala {money(priced.total)}</b>
						</span>
					)}
					<button className="btn" onClick={() => create(false)} disabled={!canSave || busy}>
						Spara utkast
					</button>
					<button className="btn btn--primary" onClick={() => create(true)} disabled={!canSave || busy}>
						{busy ? 'Sparar…' : 'Spara och bokför'}
					</button>
				</div>
				{error && <p><b>Går inte att bokföra:</b> {error}</p>}
			</div>

			<div className="table-wrap">
				<table style={{minWidth: 760}}>
					<thead><tr>
						<th>Faktura</th><th>Datum</th><th>Kund</th><th>Netto</th><th>Moms</th>
						<th>Totalt</th><th>Status</th><th>Verifikat</th><th />
					</tr></thead>
					<tbody>
						{list.map(inv => [
							<tr key={inv._id} onClick={() => setOpened(o => (o === inv._id ? null : inv._id))}
								style={{cursor: 'pointer'}}>
								<td>{opened === inv._id ? '− ' : '+ '}{inv._id}</td>
								<td>{inv.invoice_date}</td><td>{inv.customer_name ?? '—'}</td>
								<td>{money(inv.net)}</td><td>{money(inv.vat)}</td>
								<td className="num--strong">{money(inv.total)}</td>
								<td>{inv.state}{inv.rounding ? <span className="pill">avr {money(inv.rounding)}</span> : null}</td>
								<td>{inv.voucher ?? '—'}</td>
								<td>
									{inv.state === 'draft' && (
										<button className="btn btn--sm"
											onClick={e => { e.stopPropagation(); post(inv._id); }}>Bokför</button>
									)}
								</td>
							</tr>,
							...(opened === inv._id ? [<InvoiceRows key={`${inv._id}-r`} invoice={inv} />] : []),
						]).flat()}
						{!list.length && <tr><td colSpan={9}>Inga fakturor ännu.</td></tr>}
					</tbody>
				</table>
			</div>
		</>
	);
}

/** The lines, and the voucher they produced — the two things you want when a total looks odd. */
function InvoiceRows({invoice}) {
	const [voucher, setVoucher] = useState(null);
	useEffect(() => {
		if (!invoice.voucher) return;
		api(`/api/books/vouchers?month=${invoice.invoice_date.slice(0, 7)}`)
			.then(r => r.json())
			.then(vs => setVoucher(vs.find(v => v._id === invoice.voucher) ?? null))
			.catch(() => {});
	}, [invoice.voucher, invoice.invoice_date]);

	return (
		<tr><td colSpan={9}>
			<div className="md__title">Rader</div>
			<table style={{minWidth: 520, margin: '6px 0 14px'}}>
				<thead><tr><th>Artikel</th><th>Beskrivning</th><th>Antal</th><th>Konto</th><th>Moms</th><th>Netto</th></tr></thead>
				<tbody>
					{invoice.rows.map((r, i) => (
						<tr key={i}>
							<td>{r.article ?? '—'}</td><td>{r.description ?? '—'}</td>
							<td>{r.quantity}</td><td>{r.account}</td><td>{r.vat_rate} %</td>
							<td>{money(r.net)}</td>
						</tr>
					))}
				</tbody>
			</table>
			{voucher && (
				<>
					<div className="md__title">Verifikat {voucher._id}</div>
					<table style={{minWidth: 420, margin: '6px 0'}}>
						<thead><tr><th>Konto</th><th>Debet</th><th>Kredit</th></tr></thead>
						<tbody>
							{voucher.rows.map((r, i) => (
								<tr key={i}>
									<td>{r.account}</td>
									<td>{r.amount > 0 ? money(r.amount) : ''}</td>
									<td>{r.amount < 0 ? money(-r.amount) : ''}</td>
								</tr>
							))}
						</tbody>
						<tfoot><tr>
							<td>Summa</td>
							<td>{money(voucher.rows.filter(r => r.amount > 0).reduce((a, r) => a + r.amount, 0))}</td>
							<td>{money(-voucher.rows.filter(r => r.amount < 0).reduce((a, r) => a + r.amount, 0))}</td>
						</tr></tfoot>
					</table>
				</>
			)}
		</td></tr>
	);
}
